/**
 * Workout history helpers — pure, FE-only.
 *
 * Why this exists:
 *   The active-workout interface and the history views render from the SAME
 *   `workout.exercises` array. That's correct during execution (the client
 *   needs to see what's coming) but wrong in history (the trainer/client
 *   want to see what ACTUALLY happened).
 *
 *   These helpers derive a "performed projection" without touching the data
 *   model — `actual_sets` on each exercise is already the source of truth.
 */

/**
 * An exercise was performed if it has at least one logged actual set.
 */
export const isExercisePerformed = (ex) =>
  Array.isArray(ex?.actual_sets) && ex.actual_sets.length > 0;

/**
 * A workout is in "history mode" when it's been finished or marked completed.
 * History views should call this before applying the performed-only filter.
 */
export const isWorkoutCompleted = (workout) =>
  workout?.is_finished === true || workout?.status === 'completed';

/**
 * Split an exercise list into:
 *   - performed: exercises actually done (with the superset-group preservation
 *     rule applied)
 *   - skipped:   exercises that have no logged sets and aren't part of a
 *     partially-performed superset group
 *
 * Superset rule:
 *   If ANY member of a superset_group has logged sets, the whole group is
 *   shown in the performed list. Members without logged sets get a flag
 *   (_isDimmed=true) so the renderer can render them at lower opacity to
 *   keep the visual block intact while still signalling "not performed".
 *
 * Order is preserved (matches the order the client actually executed in,
 * including any coach-mode reorders applied during the session).
 *
 * @param {Object} workout
 * @returns {{ performed: Array, skipped: Array, hasSkipped: boolean }}
 */
export const partitionExercises = (workout) => {
  const exercises = Array.isArray(workout?.exercises) ? workout.exercises : [];

  // Step 1 — find every superset_group that has at least one performed member
  const performedGroups = new Set();
  for (const ex of exercises) {
    if (ex?.is_superset && ex?.superset_group && isExercisePerformed(ex)) {
      performedGroups.add(ex.superset_group);
    }
  }

  // Step 2 — partition while preserving order
  const performed = [];
  const skipped = [];
  for (const ex of exercises) {
    if (isExercisePerformed(ex)) {
      performed.push({ ...ex, _isDimmed: false });
    } else if (
      ex?.is_superset &&
      ex?.superset_group &&
      performedGroups.has(ex.superset_group)
    ) {
      // Member of a partially-performed superset group → keep visible, dim it
      performed.push({ ...ex, _isDimmed: true });
    } else {
      skipped.push(ex);
    }
  }

  return { performed, skipped, hasSkipped: skipped.length > 0 };
};
